import { Box, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';

import { NamespacesList } from 'core/enums/common';

import { TextWithLineBreaks } from 'components/shared/TextWithBreaks';

import { regularTextColor } from 'assets/styles/_palette';

import { IUserStoryTest } from '../types';

import * as Styled from './styled';

interface UserStoryTestsProps {
	userStoryTests: Array<IUserStoryTest> | null | undefined;
	isAllTextBlack?: boolean;
}

export const UserStoryTests: FC<UserStoryTestsProps> = ({
	userStoryTests,
	isAllTextBlack = false,
}) => {
	const { t: testCasesTranslate } = useTranslation(NamespacesList['testCasesStep']);

	const stringHasOnlyNumbers = (string: string) => {
		// If string contains only numbers, returns false
		const regex = new RegExp(/^\d+$/);

		return regex.test(string);
	};

	const cleanScenariosNumbering = (array: Array<string>) =>
		array.filter((value) => value && value.length > 1 && !stringHasOnlyNumbers(value));

	const cleanedTestCases = useMemo(
		() =>
			userStoryTests
				? userStoryTests.map((test) => ({
						...test,
						testScenarios: cleanScenariosNumbering(test.testScenarios ?? []),
						useCaseTitle: normalizeObjectKeys(test)['usecasetitle'],
				  }))
				: [],
		[userStoryTests]
	);

	return (
		<Styled.UserStoryTests isAllTextBlack={isAllTextBlack}>
			{cleanedTestCases?.map((test, index) => (
				<Styled.UserStoryTest key={`${test?.id} ${index}`}>
					<Styled.TestPlanTitle>
						<span>{testCasesTranslate('testPlan.title')}:</span>{' '}
						{normalizeObjectKeys(test)['usecasetitle']}
					</Styled.TestPlanTitle>
					{test && (
						<>
							{Object.entries(test.testPlan ?? {}).map(([key, value], testPlanIndex) =>
								key === 'id' ? null : (
									<Box key={`${key} ${index} ${testPlanIndex} `}>
										<Styled.TestPlanSubTitle>
											{testCasesTranslate(`testPlan.${key}`)}:
										</Styled.TestPlanSubTitle>
										<Styled.TestDescription>{value}</Styled.TestDescription>
									</Box>
								)
							)}
							<Box>
								<Styled.TestPlanSubTitle>
									{testCasesTranslate(`testScenarios.title`)}:
								</Styled.TestPlanSubTitle>
								<ol style={{ margin: '0' }}>
									{(test.testScenarios ?? []).map((scenario, scenarioIndex) => (
										<li key={`${scenario} ${index} ${scenarioIndex}`}>
											<Styled.TestDescription>{scenario}</Styled.TestDescription>
										</li>
									))}
								</ol>
							</Box>
							<Box>
								<Styled.TestPlanSubTitle>
									{testCasesTranslate(`testCases.title`)}:
								</Styled.TestPlanSubTitle>
								<ol style={{ margin: '0' }}>
									{(test.testCases ?? []).map((testCase, testCaseIndex) => (
										<li
											key={`${testCase.testCaseId} ${index} ${testCaseIndex}`}
											style={{ marginBottom: '13px' }}
										>
											<Styled.TestCaseBox>
												<Styled.TestPlanSubTitle>
													{testCase.testCaseId}: {testCase.description}
												</Styled.TestPlanSubTitle>
												<Styled.TestDescription>
													<Typography sx={{ fontSize: '14px', color: regularTextColor }}>
														{testCasesTranslate('testCases.preConditions')}:
													</Typography>
													<TextWithLineBreaks
														text={testCase.preConditions}
														prefix="preConditions"
													/>
												</Styled.TestDescription>
												<Styled.TestDescription>
													<Typography sx={{ fontSize: '14px', color: regularTextColor }}>
														{testCasesTranslate('testCases.testSteps')}:
													</Typography>
												</Styled.TestDescription>
												<ol>
													{(testCase.testSteps ?? []).map((step, stepIndex) => (
														<li key={`${step} ${index} ${testCaseIndex} ${stepIndex}`}>
															<Styled.TestDescription>{step}</Styled.TestDescription>
														</li>
													))}
												</ol>
												<Styled.TestDescription>
													<Typography sx={{ fontSize: '14px', color: regularTextColor }}>
														{testCasesTranslate('testCases.testData')}:
													</Typography>
													<TextWithLineBreaks text={testCase.testData} prefix="testData" />
												</Styled.TestDescription>
												<Styled.TestDescription>
													<Typography sx={{ fontSize: '14px', color: regularTextColor }}>
														{testCasesTranslate('testCases.expectedResult')}:
													</Typography>
													<TextWithLineBreaks
														text={testCase.expectedResult}
														prefix="expectedResults"
													/>
												</Styled.TestDescription>
											</Styled.TestCaseBox>
										</li>
									))}
								</ol>
							</Box>
						</>
					)}
				</Styled.UserStoryTest>
			))}
		</Styled.UserStoryTests>
	);
};
